import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import './ServicePage.css';

const IndustrialDesign = () => {
  const features = [
    {
      icon: 'fas fa-drafting-compass',
      title: 'Custom Kitchen Layouts',
      description: 'Tailored kitchen designs optimized for workflow efficiency and space utilization.',
      image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80'
    },
    {
      icon: 'fas fa-tools',
      title: 'Equipment Specification',
      description: 'Detailed specifications for commercial kitchen and laundry equipment.',
      image: 'https://images.unsplash.com/photo-1581092160562-40aa08e78837?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80'
    },
    {
      icon: 'fas fa-chart-line',
      title: 'Workflow Optimization',
      description: 'Strategic planning to maximize operational efficiency and productivity.',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80'
    },
    {
      icon: 'fas fa-cube',
      title: '3D Visualization',
      description: 'Advanced 3D modeling and visualization for better project understanding.',
      image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80'
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Initial Consultation',
      description: 'Understanding your requirements, space constraints, and operational needs.',
      icon: 'fas fa-comments'
    },
    {
      step: '02',
      title: 'Site Assessment',
      description: 'Detailed analysis of the existing space and infrastructure capabilities.',
      icon: 'fas fa-search'
    },
    {
      step: '03',
      title: 'Design Development',
      description: 'Creating comprehensive design solutions with 3D visualizations.',
      icon: 'fas fa-pencil-ruler'
    },
    {
      step: '04',
      title: 'Implementation',
      description: 'Professional installation and setup with quality assurance.',
      icon: 'fas fa-cogs'
    }
  ];

  const gallery = [
    'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    'https://images.unsplash.com/photo-1581092160562-40aa08e78837?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    'https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    'https://images.unsplash.com/photo-1504307651254-35680f356dfd?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    'https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    'https://images.unsplash.com/photo-1503387762-592deb58ef4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    'https://images.unsplash.com/photo-1611224923853-80b023f02d71?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
  ];

  return (
    <div className="service-page">
      {/* Hero Section */}
      <section className="service-hero parallax-bg">
        <div className="hero-background">
          <img src="https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80" alt="Industrial Design" />
        </div>
        <div className="parallax-overlay"></div>
        <div className="container-wide">
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1 }}
              className="hero-content"
            >
              <div className="breadcrumb">
                <Link to="/">Home</Link>
                <span>/</span>
                <Link to="/services">Services</Link>
                <span>/</span>
                <span>Industrial Design</span>
              </div>
              <h1>Industrial Design</h1>
              <h2>Kitchen & Laundry Equipment Solutions</h2>
              <p>Complete design solutions for commercial kitchen and laundry facilities with focus on efficiency, functionality, and modern aesthetics.</p>
              <div className="hero-buttons">
                <Link to="/contact" className="btn btn-primary">
                  Get Quote <i className="fas fa-arrow-right"></i>
                </Link>
                <a href="tel:+1234567890" className="btn btn-secondary">
                  Call Now <i className="fas fa-phone"></i>
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Service Overview */}
      <section className="service-overview section">
        <div className="container-wide">
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="section-title">Service Overview</h2>
              <p className="section-subtitle">
                Our industrial design services combine functionality with aesthetics to create efficient and modern facilities
              </p>
            </motion.div>

            <div className="overview-content">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="overview-text"
              >
                <h3>Comprehensive Design Solutions</h3>
                <p>
                  Our industrial design team specializes in creating state-of-the-art kitchen and laundry facilities 
                  that maximize efficiency while maintaining the highest standards of hygiene and safety. We understand 
                  that every facility has unique requirements, and our designs are tailored to meet your specific needs.
                </p>
                <p>
                  From initial concept to final implementation, we work closely with our clients to ensure that every 
                  aspect of the design serves a purpose. Our expertise spans across various industries including 
                  hospitality, healthcare, education, and commercial sectors.
                </p>
                <div className="key-benefits">
                  <div className="benefit-item">
                    <i className="fas fa-check-circle"></i>
                    <span>Space Optimization</span>
                  </div>
                  <div className="benefit-item">
                    <i className="fas fa-check-circle"></i>
                    <span>Energy Efficiency</span>
                  </div>
                  <div className="benefit-item">
                    <i className="fas fa-check-circle"></i>
                    <span>Compliance Standards</span>
                  </div>
                  <div className="benefit-item">
                    <i className="fas fa-check-circle"></i>
                    <span>Future-Ready Design</span>
                  </div>
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="overview-image"
              >
                <img src="https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" alt="Industrial Design Overview" />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="service-features section parallax-bg">
        <div className="hero-background">
          <img src="https://images.unsplash.com/photo-1581092160562-40aa08e78837?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80" alt="Features Background" />
        </div>
        <div className="parallax-overlay"></div>
        <div className="container-wide">
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="section-title">Key Features</h2>
              <p className="section-subtitle">
                Comprehensive design services that cover every aspect of your facility
              </p>
            </motion.div>

            <div className="features-grid">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="feature-card card-hover-lift card-glow"
                  whileHover={{ y: -10 }}
                >
                  <div className="feature-image">
                    <img src={feature.image} alt={feature.title} />
                    <div className="feature-overlay">
                      <i className={feature.icon}></i>
                    </div>
                  </div>
                  <div className="feature-content">
                    <h3>{feature.title}</h3>
                    <p>{feature.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="service-process section">
        <div className="container-wide">
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="section-title">Our Process</h2>
              <p className="section-subtitle">
                A systematic approach to delivering exceptional design solutions
              </p>
            </motion.div>

            <div className="process-timeline">
              {process.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  className="process-step"
                >
                  <div className="step-number">{step.step}</div>
                  <div className="step-icon">
                    <i className={step.icon}></i>
                  </div>
                  <div className="step-content">
                    <h3>{step.title}</h3>
                    <p>{step.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="service-gallery section parallax-bg">
        <div className="hero-background">
          <img src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80" alt="Gallery Background" />
        </div>
        <div className="parallax-overlay"></div>
        <div className="container-wide">
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="section-title">Project Gallery</h2>
              <p className="section-subtitle">
                Showcasing our industrial design expertise across various projects
              </p>
            </motion.div>

            <div className="gallery-grid">
              {gallery.map((image, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="gallery-item"
                  whileHover={{ scale: 1.05 }}
                >
                  <img src={image} alt={`Project ${index + 1}`} />
                  <div className="gallery-overlay">
                    <i className="fas fa-search-plus"></i>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="service-cta section">
        <div className="container-wide">
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="cta-content"
            >
              <h2>Ready to Transform Your Facility?</h2>
              <p>Let our industrial design experts create the perfect solution for your needs</p>
              <div className="cta-buttons">
                <Link to="/contact" className="btn btn-primary">
                  Start Your Project
                </Link>
                <Link to="/services" className="btn btn-secondary">
                  View All Services
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default IndustrialDesign;